/**
 * Copies the provided text to the system clipboard.
 * @param text - The text to copy to the clipboard
 * @returns A promise that resolves when the text has been copied
 */
export const copyTextToClipboard = async (text: string): Promise<void> => navigator.clipboard.writeText(text)

/**
 * Extracts the file extension from a filename.
 * @param fileName - The filename to extract the extension from
 * @returns The file extension (including multiple dots if present), or null if no extension exists
 */
export const getFileNameExtension = (fileName: string): string | null => {
  const fileExtension = fileName.split('.')
  if (fileExtension.length <= 1) return null
  return fileExtension.slice(1).join('.')
}

/**
 * Maps a file extension to its corresponding MIME type.
 * @param fileNameExtension - The file extension (without the leading dot)
 * @returns The MIME type string, defaults to 'text/plain' for unknown extensions
 */
export const getMIMETypeFromFileNameExtension = (fileNameExtension: string): string => {
  switch (fileNameExtension) {
    case 'csv':
      return 'text/csv'
    case 'pdf':
      return 'application/pdf'
    case 'txt':
      return 'text/plain'
    case 'json':
      return 'application/json'
    case 'xml':
      return 'application/xml'
    case 'html':
      return 'text/html'
    case 'zip':
      return 'application/zip'
    case 'xls':
      return 'application/vnd.ms-excel'
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'doc':
      return 'application/msword'
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    default:
      return 'text/plain'
  }
}

/**
 * Determines the MIME type from a filename by extracting its extension
 * and mapping it to the corresponding MIME type.
 * @param fileName - The filename to determine the MIME type for
 * @returns The MIME type string, defaults to 'text/plain' if no extension is found
 */
export const getMIMETypeFromFileName = (fileName: string): string => {
  const fileExtension = getFileNameExtension(fileName)
  return getMIMETypeFromFileNameExtension(fileExtension ?? '')
}

/**
 * Extracts the filename from a file path.
 * @param path - The file path to extract the filename from
 * @returns The filename if it contains an extension, otherwise null
 */
export const getFileNameFromPath = (path: string): string | null => {
  const fileName = path.split('/').pop()

  // The file name must contain an extension
  if (!fileName || !fileName.includes('.')) return null
  return fileName
}
